// ============================================
// API CLIENT - Backend communication
// ============================================

import { WeekSchedule, HistoryEntry } from './types.js';
import { getToken, clearAuth } from './auth.js';

// Determine API base URL based on environment
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:9292'
  : 'https://colbo.fetsh.me';

const API_ENDPOINTS = {
  getWeek: (weekStart: string) => `${API_BASE_URL}/mishmarot/week/${weekStart}`,
  updateDay: (dayDate: string) => `${API_BASE_URL}/mishmarot/day/${dayDate}`,
  toggleShift: (dayDate: string) => `${API_BASE_URL}/mishmarot/day/${dayDate}/shift`,
  toggleKindergarten: (dayDate: string) => `${API_BASE_URL}/mishmarot/day/${dayDate}/kindergarten`,
  history: () => `${API_BASE_URL}/mishmarot/history`,
  authTelegram: () => `${API_BASE_URL}/mishmarot/auth/telegram`,
  authLogout: () => `${API_BASE_URL}/mishmarot/auth/logout`,
};

/**
 * Fetch wrapper that adds Bearer token and handles 401.
 */
async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    clearAuth();
    window.location.reload();
    throw new Error('Unauthorized');
  }

  return response;
}

/**
 * Fetch week schedule from API.
 */
export async function fetchWeek(weekStart: string): Promise<WeekSchedule> {
  const response = await authFetch(API_ENDPOINTS.getWeek(weekStart));

  if (!response.ok) {
    throw new Error(`Failed to fetch week: ${response.statusText}`);
  }

  const data = await response.json();

  return {
    weekStart: data.weekStart,
    days: data.days.map((day: any) => ({
      date: day.date,
      alena: {
        earlyMorning: day.alena.earlyMorning,
        morning: day.alena.morning,
        evening: day.alena.evening
      },
      ilya: {
        earlyMorning: day.ilya.earlyMorning,
        evening: day.ilya.evening,
        night: day.ilya.night
      },
      kindergarten: day.kindergarten
    }))
  };
}

/**
 * Toggle a single shift via API.
 */
export async function toggleShift(
  dayDate: string,
  person: 'alena' | 'ilya',
  shift: string
): Promise<void> {
  const response = await authFetch(API_ENDPOINTS.toggleShift(dayDate), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      person,
      shift
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to toggle shift');
  }
}

/**
 * Toggle kindergarten via API.
 */
export async function toggleKindergarten(dayDate: string): Promise<void> {
  const response = await authFetch(API_ENDPOINTS.toggleKindergarten(dayDate), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to toggle kindergarten');
  }
}

/**
 * Update entire day via API.
 */
export async function updateDay(day: any): Promise<void> {
  const response = await authFetch(API_ENDPOINTS.updateDay(day.date), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(day)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update day');
  }
}

/**
 * Fetch change history from API.
 */
export async function fetchHistory(): Promise<HistoryEntry[]> {
  const response = await authFetch(API_ENDPOINTS.history());

  if (!response.ok) {
    throw new Error(`Failed to fetch history: ${response.statusText}`);
  }

  const data = await response.json();
  return data.history || [];
}

/**
 * Login via Telegram.
 */
export async function loginWithTelegram(telegramData: Record<string, unknown>): Promise<{ token: string; user: { first_name: string } }> {
  const response = await fetch(API_ENDPOINTS.authTelegram(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(telegramData),
  });

  if (!response.ok) {
    throw new Error('Telegram authentication failed');
  }

  return response.json();
}

/**
 * Logout — delete token on backend.
 */
export async function logout(): Promise<void> {
  const token = getToken();
  if (token) {
    await fetch(API_ENDPOINTS.authLogout(), {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    }).catch(() => {});
  }
}
